/**
 * DirectiveToggle Component
 * Toggle button for analysis directive options with accessibility
 */

import React from 'react';
import { DirectiveItem } from '../../types';
import { Tooltip } from './Tooltip';

interface DirectiveToggleProps {
  item: DirectiveItem;
  active: boolean;
  onToggle: () => void;
  disabled: boolean;
}

export const DirectiveToggle: React.FC<DirectiveToggleProps> = ({
  item,
  active,
  onToggle,
  disabled,
}) => {
  const baseClass = `flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all border text-left w-full group relative`;

  const activeClass = active
    ? 'bg-[#00d4b8]/10 border-[#00d4b8]/30 shadow-[0_4px_20px_-4px_rgba(0,212,184,0.15)] ring-1 ring-[#00d4b8]/10'
    : 'bg-white border-transparent hover:border-gray-200 hover:bg-gray-50';

  const disabledClass = disabled
    ? 'opacity-50 cursor-not-allowed'
    : 'cursor-pointer active:scale-[0.98]';

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`${baseClass} ${activeClass} ${disabledClass}`}
      role="checkbox"
      aria-checked={active}
      aria-describedby={`${item.id}-desc`}
    >
      {/* Glow effect when active */}
      {active && (
        <div className="absolute top-0 right-0 w-24 h-24 bg-[#00d4b8]/5 rounded-full -mr-12 -mt-12 blur-2xl" aria-hidden="true" />
      )}

      {/* Checkbox indicator */}
      <div
        className={`mt-0.5 w-4 sm:w-5 h-4 sm:h-5 rounded-md sm:rounded-lg border-2 flex items-center justify-center transition-all shrink-0 ${
          active
            ? 'bg-[#00d4b8] border-[#00d4b8] shadow-[0_0_8px_rgba(0,212,184,0.5)]'
            : 'bg-white border-gray-300 group-hover:border-gray-400'
        }`}
        aria-hidden="true"
      >
        {active && (
          <svg
            className="w-2.5 sm:w-3 h-2.5 sm:h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="4"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>

      {/* Label and description */}
      {item.hint ? (
        <Tooltip text={item.hint} position="bottom">
          <div className="flex flex-col relative z-10 min-w-0">
            <span
              className={`text-[10px] sm:text-[11px] font-black uppercase tracking-tight transition-colors truncate ${
                active ? 'text-[#007a6d]' : 'text-gray-700'
              }`}
            >
              {item.label}
            </span>
            <span
              id={`${item.id}-desc`}
              className="text-[8px] sm:text-[9px] font-bold text-gray-400 uppercase tracking-tighter leading-none mt-0.5 sm:mt-1 truncate"
            >
              {item.desc}
            </span>
          </div>
        </Tooltip>
      ) : (
        <div className="flex flex-col relative z-10 min-w-0">
          <span
            className={`text-[10px] sm:text-[11px] font-black uppercase tracking-tight transition-colors truncate ${
              active ? 'text-[#007a6d]' : 'text-gray-700'
            }`}
          >
            {item.label}
          </span>
          <span
            id={`${item.id}-desc`}
            className="text-[8px] sm:text-[9px] font-bold text-gray-400 uppercase tracking-tighter leading-none mt-0.5 sm:mt-1 truncate"
          >
            {item.desc}
          </span>
        </div>
      )}
    </button>
  );
};
